import { useEffect, useMemo, useState } from "react";
import { CheckIcon, XIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface InlineEditableTextareaProps {
  value: string;
  onSave: (nextValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  textareaClassName?: string;
}

export function InlineEditableTextarea({
  value,
  onSave,
  placeholder,
  disabled,
  className,
  textareaClassName,
}: InlineEditableTextareaProps) {
  const [draft, setDraft] = useState<string>(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const isDirty = useMemo(() => draft !== (value ?? ""), [draft, value]);

  const handleCancel = () => {
    setDraft(value ?? "");
  };

  const handleSave = () => {
    if (!isDirty) return;
    onSave(draft);
  };

  return (
    <div className={cn(className, "flex items-start gap-2 w-full")}>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(textareaClassName, "w-full")}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            handleSave();
          }
          if (e.key === "Escape") {
            handleCancel();
          }
        }}
        onBlur={handleSave}
      />
      {isDirty && (
        <div className="flex flex-col gap-1 pt-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={handleSave}
            disabled={disabled}
            className="size-7"
          >
            <CheckIcon className="size-3" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={handleCancel}
            disabled={disabled}
            className="size-7"
          >
            <XIcon className="size-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default InlineEditableTextarea;
